/**
 * getPageWindow utility
 *
 * Builds a sliding-window page list around the current page for pagination UI.
 *
 * @module domains/personnel/presentation/utils/getPageWindow
 */

export type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

/**
 * Builds a sliding-window page list around the current page.
 *
 * - Returns empty array when totalPages is 0 or negative.
 * - Returns all pages when totalPages <= 5.
 * - Otherwise: first page + (optional ellipsis) + window of up to 3 pages
 *   around current + (optional ellipsis) + last page.
 *
 * @param currentPage - 1-indexed current page
 * @param totalPages - total number of pages
 * @returns Array of page numbers and ellipsis markers
 */
export const getPageWindow = (currentPage: number, totalPages: number): PageItem[] => {
  if (totalPages <= 0) return [];
  if (totalPages === 1) return [1];
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const windowStart = Math.max(2, currentPage - 1);
  const windowEnd = Math.min(totalPages - 1, currentPage + 1);

  const items: PageItem[] = [1];
  if (windowStart > 2) items.push('ellipsis-start');
  for (let page = windowStart; page <= windowEnd; page += 1) items.push(page);
  if (windowEnd < totalPages - 1) items.push('ellipsis-end');
  items.push(totalPages);

  return items;
};
